import { forwardRef, useId, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string | undefined
  error?: string | undefined
  hint?: string | undefined
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, disabled, rows = 4, ...props }, ref) => {
    const generatedId = useId()
    const textareaId = id || generatedId
    const errorId = `${textareaId}-error`
    const hintId = `${textareaId}-hint`
    const describedBy = error ? errorId : hint ? hintId : undefined

    return (
      <div className="w-full flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={textareaId}
            className="text-xs font-semibold uppercase tracking-wider text-fg-subtle"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={cn(
            'w-full rounded-sm border bg-surface-1 px-4 py-2.5 text-base sm:text-sm text-fg placeholder:text-fg-muted resize-y',
            'transition-colors duration-150',
            'border-border focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong',
            error && 'border-error focus:border-error focus:ring-error',
            disabled && 'opacity-50 cursor-not-allowed bg-surface-2',
            className
          )}
          {...props}
        />
        {hint && !error && (
          <span id={hintId} className="text-xs text-fg-muted">
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className="text-xs font-medium text-error" role="alert">
            {error}
          </span>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
